import React from 'react';

import * as Store from '@apply/store';
import { LoanType } from '@apply/constants';
import { Accordion, ShowIf, buildTypeString, addDays, formatCurrency, DataRow, PropertySubheader, formatDate } from '@apply/ui';

export interface NonRentalPropertyReviewProps {
    property: Store.PropertiesStore.Types.Property;
    index: number;
    apiId?: string;
    printMode: boolean;
    formCompleted: boolean;
    loanType?: LoanType;
    token: string;
    collapsable: boolean;
}

export const NonRentalPropertyReview: React.FC<NonRentalPropertyReviewProps> = ({
    property,
    index,
    apiId,
    printMode,
    formCompleted,
    loanType,
    token,
    collapsable,
}) => {
    const provideLaterAdvisory = Store.PropertiesStore.Types.constructReviewFormsAdvisoryMessagePrefix(loanType, property);

    const isVarious = property?.structureType === 'Various';
    // Various only asks about income-restricted units once the unit count reaches 5.
    const isIncomeRestrictedAsked = !isVarious || (property?.nonRental?.numberUnits ?? 0) >= 5;

    const isDateOfAcquisitionInFuture = (): boolean => {
        const dateOfAcquisition = new Date(property?.nonRental?.acquisitionDate);

        const today = new Date();

        return dateOfAcquisition.getTime() > today.getTime();
    };

    const isDateOfAcquisitionOverNinetyDays = (): boolean => {
        const dateOfAcquisition = new Date(property?.nonRental?.acquisitionDate);
        const ninetyDaysFromAcquisition = addDays(dateOfAcquisition, 90);

        const today = new Date();

        return today.getTime() > ninetyDaysFromAcquisition.getTime();
    };

    const isChallengingTimeTable = (): boolean => {
        if (property?.nonRental?.isAquisitionFinancing !== true) return false;

        const dateOfAcquisition = new Date(property?.nonRental?.acquisitionDate);
        const twentyOneDaysBeforeAcquisition = addDays(dateOfAcquisition, -21);

        const today = new Date();

        return today.getTime() > twentyOneDaysBeforeAcquisition.getTime();
    };

    const wasBuiltBefore1981 = (): boolean => {
        return property?.nonRental?.yearBuilt < 1981;
    };

    const calloutRedNote = (showIf: boolean, text: string) => {
        return (
            <ShowIf showIf={showIf && printMode}>
                <div className='row'>
                    <div style={{ width: '50%' }}>&nbsp;</div>
                    <div style={{ width: '50%', color: 'red' }}>
                        <b>{text}</b>
                    </div>
                </div>
            </ShowIf>
        );
    };

    const isPurchaseDateLessThanTwoYears = (): boolean => {
        if (!property?.rental?.purchaseDate) return false;

        const purchaseYearMonthDay = property?.rental?.purchaseDate.replace('-', '').replace('-', '');

        const twoYearsBeforeToday = new Date();
        twoYearsBeforeToday.setFullYear(twoYearsBeforeToday.getFullYear() - 2);

        const twoYearsBeforeYearMonthDay = `${twoYearsBeforeToday.getFullYear()}${(twoYearsBeforeToday.getMonth() + 1)
            .toString()
            .padStart(2, '0')}${twoYearsBeforeToday.getDate().toString().padStart(2, '0')}`;
        return purchaseYearMonthDay > twoYearsBeforeYearMonthDay; // i.e. '20220619' > '20220630' (YYYYMMDD)
    };

    return (
        <>
            <div className='scrollable-content'>
                <div
                    className='accordion gray-adjust'
                    data-accordion=''
                    data-allow-all-closed='true'
                    data-multi-expand='true'
                >
                    <Accordion
                        title={
                            <PropertySubheader
                                index={index}
                                property={property}
                                apiId={apiId}
                                formCompleted={formCompleted}
                                token={token}
                                printMode={printMode}
                            />
                        }
                        isCollapsable={collapsable}
                    >
                        <ShowIf showIf={loanType !== LoanType.Land}>
                            <DataRow value={property?.structureType}>Property structure</DataRow>
                            <ShowIf showIf={(property?.nonRental?.numberUnits ?? 0) > 0}>
                                <DataRow value={property?.nonRental?.numberUnits}>Units</DataRow>
                                <ShowIf showIf={[LoanType.FixFlip, LoanType.SubNoteFixFlip].includes(loanType) && isIncomeRestrictedAsked}>
                                    <DataRow value={property?.nonRental?.unitsIncomeRestricted}>
                                        Units subject to income restricted federal, state, or local housing program
                                    </DataRow>
                                </ShowIf>
                            </ShowIf>
                            <ShowIf showIf={isVarious}>
                                <DataRow value={property?.nonRental?.variousUnitsDescription}>Describe the units</DataRow>
                            </ShowIf>
                        </ShowIf>
                        <DataRow value={property?.address}>Property Address</DataRow>
                        <DataRow value={property?.nonRental?.legalDescription}>Legal Description</DataRow>
                        <DataRow value={property?.nonRental?.landSize}>Land Size</DataRow>

                        <ShowIf showIf={loanType === LoanType.Land}>
                            <DataRow value={property?.nonRental?.currentPlattedLots}>Current number of platted lots</DataRow>
                            <DataRow value={property?.nonRental?.plannedPlattedLots}>Planned number of platted lots</DataRow>
                            <DataRow value={property?.nonRental?.intendedWork}>
                                Briefly describe the work you intend to do
                            </DataRow>
                            <DataRow value={property?.nonRental?.requiredZoningOrEntitlement}>
                                Does the work require any zoning or entitlement changes?
                            </DataRow>
                            <ShowIf showIf={property.nonRental?.requiredZoningOrEntitlement === true}>
                                <DataRow value={property?.nonRental?.describeRequiredInspections}>
                                    Describe applicable required inspections
                                </DataRow>
                            </ShowIf>
                            <DataRow value={property?.nonRental?.willBuildOnLandYourself}>
                                Do you plan to build on all of the land yourself?
                            </DataRow>
                            <ShowIf showIf={property.nonRental?.willBuildOnLandYourself === false}>
                                <DataRow value={property?.nonRental?.exitPlans}>Please provide detail on exit plans</DataRow>
                            </ShowIf>
                            <DataRow value={property?.nonRental?.startedSiteWork}>
                                Have you started performing any site work?
                            </DataRow>
                        </ShowIf>

                        <ShowIf showIf={loanType === LoanType.FixFlip || loanType === LoanType.SubNoteFixFlip}>
                            <DataRow value={property?.nonRental?.yearBuilt}>Year Built</DataRow>
                            <DataRow value={buildTypeString(property?.nonRental?.buildType)}>
                                Is it a Site Built or Manufactured home?
                            </DataRow>

                            {calloutRedNote(wasBuiltBefore1981(), 'ASBESTOS RISK')}
                            <DataRow value={property?.nonRental?.undergroundStorageTanks ?? 'Unsure'}>
                                Any underground storage tanks?
                            </DataRow>
                            <DataRow value={formatDate(new Date(property?.nonRental?.acquisitionDate))}>
                                Date of acquisition (actual or expected)
                            </DataRow>
                            {calloutRedNote(isDateOfAcquisitionOverNinetyDays(), 'OWNED A WHILE')}
                            {calloutRedNote(isChallengingTimeTable(), 'CHALLENGING TIMETABLE')}
                            <DataRow value={property?.nonRental?.isForeclosure}>
                                {isDateOfAcquisitionInFuture() ? <>Is</> : <>Was</>} the seller HUD, Bank, or other institutional
                                foreclosure?
                            </DataRow>
                            <ShowIf showIf={isDateOfAcquisitionInFuture()}>
                                <DataRow value={property?.nonRental?.isAquisitionFinancing}>
                                    Is acquisition funding required at closing?
                                </DataRow>
                                <ShowIf showIf={property.nonRental?.isAquisitionFinancing === false}>
                                    <DataRow value={property.nonRental?.anyExistingDebt}>Existing Debt on Property?</DataRow>
                                    <DataRow value={formatCurrency(property.nonRental?.currentDebt, 0)}>
                                        Current debt on property (enter $0 if no debt you owe)
                                    </DataRow>
                                </ShowIf>
                            </ShowIf>
                            <ShowIf showIf={!isDateOfAcquisitionInFuture()}>
                                <DataRow value={formatCurrency(property.nonRental?.currentDebt, 0)}>
                                    Current debt on property (enter $0 if no debt you owe)
                                </DataRow>
                            </ShowIf>
                            <DataRow value={property?.nonRental?.intendedWork}>
                                Briefly describe the work you intend to do
                            </DataRow>
                            <DataRow value={formatCurrency(property?.nonRental?.expectedListingPrice, 0)}>
                                {property.structureType === 'Duplex' ||
                                property.structureType === 'Triplex' ||
                                property.structureType === 'Quadplex' ||
                                property.structureType === '5+ Unit'
                                    ? 'Total Expected Sale Price'
                                    : 'Expected Listing Price'}
                            </DataRow>
                            <DataRow value={formatCurrency(property?.nonRental?.targetLoanRequest, 0)}>
                                Target Loan Request
                            </DataRow>
                        </ShowIf>
                        <ShowIf showIf={loanType !== LoanType.SubNoteConstruction}>
                            <DataRow value={property?.nonRental?.generalContractorWillWork}>
                                {' '}
                                Will a third-party General Contractor work on this?
                            </DataRow>
                        </ShowIf>
                        <ShowIf showIf={property?.nonRental?.generalContractorWillWork}>
                            <DataRow value={property?.nonRental?.generalContractorName}>G.C. Company Name</DataRow>
                            <DataRow value={property?.nonRental?.generalContractorResume}>G.C. Resume</DataRow>
                        </ShowIf>
                        <ShowIf showIf={loanType !== LoanType.SubNoteConstruction}>
                            <ShowIf showIf={!property?.nonRental?.generalContractorWillWork}>
                                <DataRow value={property?.nonRental?.contactIsPrimary
                                    ? [property.nonRental.contactName?.first, property.nonRental.contactName?.middle, property.nonRental.contactName?.last].filter(Boolean).join(' ')
                                    : property?.nonRental?.name}>Contact name</DataRow>
                                <DataRow value={property?.nonRental?.email}>Contact email</DataRow>
                                <DataRow value={property?.nonRental?.phone}>Contact phone</DataRow>
                            </ShowIf>
                        </ShowIf>
                        <ShowIf
                            showIf={
                                loanType !== LoanType.Land &&
                                loanType !== LoanType.FixFlip &&
                                loanType !== LoanType.SubNoteFixFlip
                            }
                        >
                            <DataRow value={property?.nonRental?.budget}>Budget</DataRow>
                        </ShowIf>
                        <ShowIf showIf={loanType === LoanType.Land}>
                            <DataRow value={property?.nonRental?.landBudget}>Budget</DataRow>
                        </ShowIf>
                        <ShowIf showIf={loanType === LoanType.FixFlip || loanType === LoanType.SubNoteFixFlip}>
                            <DataRow value={property?.nonRental?.fixFlipBudget}>Budget</DataRow>
                        </ShowIf>

                        <ShowIf
                            showIf={
                                loanType !== LoanType.Land &&
                                loanType !== LoanType.FixFlip &&
                                loanType !== LoanType.SubNoteFixFlip
                            }
                        >
                            <DataRow value={property?.nonRental?.plans}>Plans</DataRow>
                        </ShowIf>
                        <ShowIf showIf={loanType !== LoanType.Land}>
                            <DataRow value={property?.nonRental?.specSheet}>Spec sheet</DataRow>
                        </ShowIf>
                        <ShowIf showIf={property.nonRental?.specSheet?.handleMode === 'willnotprovide'}>
                            <p style={{ color: 'red', marginLeft: 20 }}>
                                NOTE: Without this detail, the appraiser is likely to assume the lowest grade materials.
                            </p>
                        </ShowIf>
                        <ShowIf showIf={loanType !== LoanType.FixFlip && loanType !== LoanType.SubNoteFixFlip}>
                            <DataRow value={property?.nonRental?.survey}>Survey</DataRow>
                        </ShowIf>
                        <ShowIf showIf={loanType === LoanType.Construction || loanType === LoanType.SubNoteConstruction}>
                            <DataRow value={property?.nonRental?.preSold}>
                                {property.structureType === 'Duplex' ||
                                property.structureType === 'Triplex' ||
                                property.structureType === 'Quadplex' ||
                                property.structureType === '5+ Unit'
                                    ? 'Any pre-sales'
                                    : 'Pre-sold'}
                            </DataRow>
                            <ShowIf showIf={property?.nonRental?.preSold}>
                                <DataRow value={property?.nonRental?.presaleContract}>
                                    {property.structureType === 'Duplex' ||
                                    property.structureType === 'Triplex' ||
                                    property.structureType === 'Quadplex' ||
                                    property.structureType === '5+ Unit'
                                        ? 'Pre-sales contract'
                                        : 'Pre-sold contract'}
                                </DataRow>
                            </ShowIf>
                            <ShowIf showIf={property.nonRental?.preSold === false}>
                                <DataRow value={formatCurrency(property?.nonRental?.expectedListingPrice, 0)}>
                                    {property.structureType === 'Duplex' ||
                                    property.structureType === 'Triplex' ||
                                    property.structureType === 'Quadplex' ||
                                    property.structureType === '5+ Unit'
                                        ? 'Total Expected Sale Price'
                                        : 'Expected Listing Price'}
                                </DataRow>
                            </ShowIf>
                        </ShowIf>

                        <ShowIf showIf={!!provideLaterAdvisory}>
                            <div className='row'>
                                <div
                                    style={{
                                        paddingRight: '50px',
                                        paddingLeft: '50px',
                                    }}
                                >
                                    <strong>
                                        <em>NOTE:</em>
                                    </strong>
                                    &nbsp; {provideLaterAdvisory} are/is needed to process your application and order a valuation.
                                    Please submit ASAP via&nbsp;
                                    <a href='mailto:team@builderfinance.com'>team@builderfinance.com</a>
                                    &nbsp; or&nbsp;
                                    <a
                                        href='https://fileshare.bfiweb.com'
                                        target='_blank'
                                    >
                                        https://fileshare.bfiweb.com
                                    </a>
                                </div>
                            </div>
                        </ShowIf>
                        <ShowIf
                            showIf={
                                loanType !== LoanType.FixFlip &&
                                loanType !== LoanType.SubNoteFixFlip &&
                                loanType !== LoanType.Land
                            }
                        >
                            <DataRow value={property?.nonRental?.constructionStarted}>Construction started</DataRow>
                        </ShowIf>
                        <ShowIf showIf={loanType === LoanType.FixFlip || loanType === LoanType.SubNoteFixFlip}>
                            <DataRow value={property.nonRental?.constructionStarted}>Have you started work?</DataRow>
                        </ShowIf>
                    </Accordion>
                </div>
            </div>
        </>
    );
};
